import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { batchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Loader2, CheckCircle2, AlertCircle, Video, Sparkles, Clock,
  ArrowRight, RefreshCcw, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const statusIcon = (status) => {
  switch (status) {
    case 'ready': case 'completed': return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
    case 'failed': return <AlertCircle className="w-4 h-4 text-red-400" />;
    case 'queued': return <Clock className="w-4 h-4 text-muted-foreground" />;
    case 'partial': return <AlertCircle className="w-4 h-4 text-yellow-400" />;
    default: return <Loader2 className="w-4 h-4 animate-spin text-primary" />;
  }
};

const statusBadge = (status) => {
  const map = {
    processing: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    failed: 'bg-red-500/10 text-red-400 border-red-500/20',
    partial: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    queued: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    ready: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    extracting_audio: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    transcribing: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };
  return map[status] || map.processing;
};

export default function PipelinePage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadBatches();
    const interval = setInterval(loadBatches, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadBatches = async () => {
    try {
      const res = await batchAPI.all();
      setBatches(res.data);
    } catch (err) {
      if (loading) toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeBatches = batches.filter(b => b.status === 'processing');
  const completedBatches = batches.filter(b => b.status !== 'processing');

  return (
    <div className="p-8 space-y-8" data-testid="pipeline-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pipeline</h1>
          <p className="text-muted-foreground mt-2">Track your batch uploads and generation jobs in real-time.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadBatches} data-testid="refresh-pipeline">
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {batches.length === 0 ? (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="no-batches">
          <CardContent className="p-12 text-center">
            <Layers className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-semibold mb-2">No Batch Jobs Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Upload multiple videos at once or use "Generate Selected" from the history page.</p>
            <Button onClick={() => navigate('/upload')} className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25" data-testid="go-upload-from-pipeline">
              Go to Upload
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Active Batches */}
          {activeBatches.length > 0 && (
            <div className="space-y-4">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                Active Jobs ({activeBatches.length})
              </h2>
              {activeBatches.map((batch) => (
                <BatchCard key={batch.id} batch={batch} navigate={navigate} />
              ))}
            </div>
          )}

          {/* Completed Batches */}
          {completedBatches.length > 0 && (
            <div className="space-y-4">
              <h2 className="font-heading text-lg font-semibold text-muted-foreground">
                Completed ({completedBatches.length})
              </h2>
              {completedBatches.map((batch) => (
                <BatchCard key={batch.id} batch={batch} navigate={navigate} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BatchCard({ batch, navigate }) {
  const progress = batch.total > 0 ? Math.round(((batch.completed + batch.failed) / batch.total) * 100) : 0;
  const isActive = batch.status === 'processing';
  const isTranscription = batch.type === 'transcription';

  return (
    <Card
      className={`bg-zinc-900/60 border-white/5 transition-all duration-300 ${isActive ? 'border-primary/20' : ''}`}
      data-testid={`batch-card-${batch.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isTranscription ? 'bg-indigo-500/10' : 'bg-purple-500/10'}`}>
              {isTranscription ? <Video className="w-5 h-5 text-indigo-400" strokeWidth={1.5} /> : <Sparkles className="w-5 h-5 text-purple-400" strokeWidth={1.5} />}
            </div>
            <div>
              <CardTitle className="font-heading text-base">
                {isTranscription ? 'Batch Transcription' : 'Batch Generation'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {batch.total} videos &middot; {new Date(batch.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          <Badge variant="outline" className={statusBadge(batch.status)}>
            {statusIcon(batch.status)}
            <span className="ml-1 capitalize">{batch.status}</span>
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{batch.completed + batch.failed} of {batch.total} processed</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Video pipeline list */}
        <div className="space-y-2">
          {batch.videos?.map((video, index) => {
            const isCurrent = batch.current_video_id === video.id;
            return (
              <div
                key={video.id}
                className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                  isCurrent ? 'bg-primary/5 border border-primary/20' : 'bg-secondary/20 border border-white/5'
                } ${video.status === 'ready' ? 'cursor-pointer hover:border-white/10' : ''}`}
                onClick={() => video.status === 'ready' ? navigate(`/video/${video.id}`) : null}
                data-testid={`pipeline-video-${video.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center justify-center w-6 h-6 shrink-0">
                    {video.status === 'ready' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : video.status === 'failed' ? (
                      <AlertCircle className="w-4 h-4 text-red-400" />
                    ) : video.status === 'queued' ? (
                      <div className="w-3 h-3 rounded-full border-2 border-muted-foreground/30" />
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{video.filename}</p>
                    {isCurrent && (
                      <p className="text-[10px] text-primary mt-0.5">
                        {video.status === 'extracting_audio' ? 'Extracting audio...' :
                         video.status === 'transcribing' ? 'Transcribing...' : 'Processing...'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-[10px] ${statusBadge(video.status)}`}>
                    {video.status === 'queued' ? 'Queued' :
                     video.status === 'ready' ? 'Done' :
                     video.status === 'failed' ? 'Failed' :
                     video.status === 'extracting_audio' ? 'Audio' :
                     video.status === 'transcribing' ? 'STT' : 'Processing'}
                  </Badge>
                  {video.status === 'ready' && (
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 pt-2 border-t border-white/5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" /> {batch.completed} completed
          </span>
          {batch.failed > 0 && (
            <span className="flex items-center gap-1">
              <AlertCircle className="w-3 h-3 text-red-400" /> {batch.failed} failed
            </span>
          )}
          {batch.total - batch.completed - batch.failed > 0 && isActive && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {batch.total - batch.completed - batch.failed} remaining
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
